class ProductAccessories extends HTMLElement {
  connectedCallback() {
    if (!this.dataset.productHandle) {
      return;
    }

    onLibReady(['vue', 'swiper'], this.#init.bind(this));
  }

  #init() {
    new Vue({
      el: this,
      data: {
        root: this,
        offers: [],
        appliedDiscountCodes: Shopify.discount.appliedDiscountCodes,
        addingToCart: null,
      },
      template: `
          <div class="product__accessories section" v-if="offers.length">
            <div class="product__accessories-inner">
              <h2 class="section-title">It might come in handy</h2>

              <div class="product__accessories-slider">
                <div class="swiper-container" ref="slider">
                  <div class="swiper-wrapper">
                    <template v-for="offer in offers">
                      <div class="swiper-slide" :key="offer.id">
                          <div class="product__accessories-product">
                              <a :href="offer.url" style="position: absolute; top: 0; left: 0; right: 0; bottom: 0;"></a>

                              <div class="product__accessories-product-img">
                                  <img 
                                  :srcset="formatImage(offer.featuredImage, '150x150')" 
                                  src="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
                                  loading="lazy"
                                  alt=""
                                  width="150"
                                  height="150"
                                  >
                              </div>
                          
                              <div class="product__accessories-product-information">
                                  <p class="product__accessories-product-title">
                                  {{ offer.title }}
                                  </p>
                                  
                                  <span class="product__accessories-product-price" v-html="formatMoney(offer.price)" />
                                  
                                  <button
                                      class="button button--orange"
                                      :class="{ 'button--disabled': !offer.available, 'loading': addingToCart === offer.id }"
                                      @click.prevent="handleAddToCart(offer)"
                                      aria-label="Add to cart"
                                      v-html="window.Shopify.icons.bag"
                                  />
                              </div>
                          </div>
                      </div>
                  </template>
                </div>

                <div class="slider-scrollbar" ref="sliderScrollbar"></div>
              </div>

              <div class="slider-button-prev" ref="sliderBtnPrev" v-html="Shopify.icons.arrowPrev" />
              <div class="slider-button-next" ref="sliderBtnNext" v-html="Shopify.icons.arrowNext" />
            </div>
          </div>
        `,

      async mounted() {
        this.setupEventListeners();

        const currentProductVariantId = this.getCurrentProductVariantId();
        const productHandle = this.root.dataset.productHandle;

        this.product = await Shopify.shopifyService.fetchProduct(productHandle);
        this.offers = await this.getOffers(currentProductVariantId);

        setTimeout(this.initSlider, 1000);
      },

      methods: {
        setupEventListeners() {
          document.addEventListener('product.variant.change', async event => {
            this.offers = await this.getOffers(event.detail.variantId);
          });

          Shopify.discount.onUpdated(async payload => {
            this.appliedDiscountCodes = payload;
            const currentProductVariantId = this.getCurrentProductVariantId();
            this.offers = await this.getOffers(currentProductVariantId);
          });
        },

        getOfferIds(productVariantId) {
          const productVariant = this.product.variants.find(variant => variant.id === productVariantId);
          const variantOffers = productVariant?.bundle_offers ? JSON.parse(productVariant.bundle_offers) : null;
          const productOffers = this.product.bundle_offers ? JSON.parse(this.product.bundle_offers) : null;

          return variantOffers || productOffers || [];
        },

        async getOffers(productVariantId) {
          if (!this.product) {
            return [];
          }

          const offers = [];

          const accessoriesHandles = this.root.dataset.accessoriesOffers;

          if (accessoriesHandles) {
            const responses = await Promise.all(
              accessoriesHandles.split(',').map(handle => Shopify.shopifyService.fetchProduct(handle)),
            );

            const accessoriesOffers = responses
              .filter(response => response)
              .map(response => {
                const variant = response.variants[0];
                return {
                  ...variant,
                  title: response.title,
                  featuredImage: variant.featured_image?.src || response.featured_image,
                  url: response.url,
                };
              });

            offers.push(...accessoriesOffers);
          }

          const offerIds = this.getOfferIds(productVariantId);

          if (this.root.dataset.offers) {
            const ids = this.root.dataset.offers
              .split(',')
              .filter(id => id)
              .map(id => `gid://shopify/ProductVariant/${id}`);
            offerIds.unshift(...ids);
          }

          if (!offerIds.length && !offers.length) {
            return [];
          }

          const response = await Shopify.shopifyService.fetchProductVariants(offerIds);
          const offersPart =
            response.data?.nodes
              .filter(node => node)
              .map(node => {
                return {
                  id: Number(node.id.replace('gid://shopify/ProductVariant/', '')),
                  available: node.availableForSale,
                  price: node.price.amount * 100,
                  title: node.product.title + ' ' + node.title.replace('Default Title', ''),
                  featuredImage: node.image?.originalSrc || node.product.featuredImage?.originalSrc,
                  arrives: node.arrives ? node.arrives.value : null,
                  restock: node.restock ? node.restock.value : null,
                  ships_from: node.ships_from ? node.ships_from.value : null,
                  product_handle: node.product.handle,
                  sku: node.sku,
                  options: node.selectedOptions.map(option => option.value),
                  options_title: node.selectedOptions.map(option => option.name),
                  product: {
                    ...node.product,
                    collections: node.product.collections.edges.map(edge => ({
                      id: Number(edge.node.id.replace('gid://shopify/Collection/', '')),
                      handle: edge.node.handle,
                    })),
                  },
                };
              }) || [];

          offers.unshift(...offersPart);

          offers.forEach(variant => {
            const discountCode = this.appliedDiscountCodes.find(code =>
              Shopify.discount.isProductEligibleForDiscount(variant.product, code),
            );

            if (discountCode) {
              variant.price = Shopify.discount.getPriceWithDiscount(variant.price, discountCode);
            }
          });

          return [...new Map(offers.map(item => [item.id, item])).values()].filter(offer => offer.available);
        },

        handleAddToCart(variant) {
          if (this.addingToCart) return;
          this.addingToCart = variant.id;

          window.cartDrawer
            .add({
              items: [
                {
                  id: variant.id,
                  quantity: 1,
                  properties: {
                    DETAILS: getDetails(variant),
                  },
                },
              ],
            })
            .then(() => {
              this.addingToCart = null;
            });
        },

        getCurrentProductVariantId() {
          const href = window.location.href;
          const index = href.search(/\?/) + 1;
          const newLocation = href.substr(0, index) + href.slice(index).replace(/\?/g, '&');
          history.pushState(null, null, newLocation);

          const queryString = window.location.search;
          const urlParams = new URLSearchParams(queryString);
          return Number(urlParams.get('variant'));
        },

        initSlider() {
          if (!this.$refs.slider) {
            return;
          }

          new Swiper(this.$refs.slider, {
            speed: 500,
            slidesPerView: 'auto',
            observer: true,
            watchOverflow: true,
            navigation: {
              nextEl: this.$refs.sliderBtnNext,
              prevEl: this.$refs.sliderBtnPrev,
            },
            scrollbar: {
              el: this.$refs.sliderScrollbar,
              draggable: true,
            },
          });
        },
      },
    });
  }
}

customElements.define('product-accessories', ProductAccessories);
